// İSG Portal — İSG Uzmanı Panosu (Expert Dashboard — tenant-scoped, S34) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §34.0–§34.2. Backend kanonu (`backend/src/Isgportal.Permits/Models/
// ExpertDashboardDtos.cs` + `Isgportal.Api/Controllers/DashboardController.cs`); burada
// **varsayım YOK** — alan adları/nullability doğrudan sözleşmeden türetilmiştir. Serileştirme
// camelCase (§1).
//
// **Yeni modül YOK (§34 girişi, sahip kararı).** İki uç da uç kapısı olarak `permits/list`
// taşır; alan-bazlı kapılar (§34 giriş tablosu) **null** olarak yansır — `null` = "yetkin yok/
// bilinmiyor", `0`/`[]` = "yok". İstemci ikisini AYRI gösterir (§28.2 disiplini — bir tile'ı
// `null`'da GİZLER, `0` BASMAZ).
//
// **Supplier panosundan (§28) AYRIDIR** — `supplierDashboard.ts` bu modülün karşılığı DEĞİLDİR
// (farklı principal, farklı sayım kuralları; §34 girişi).
//
// **Önbellek.** Yanıtlar `Cache-Control: no-store` (principal'a özel — sunucu notu); istemci
// `staleTime` önerisi 60 sn (§34 girişi), sorgu anahtarı kullanıcı kimliğini İÇERMEZ burada
// (tek kullanıcı principal'ı zaten token'dan — ayrı kullanıcılar ayrı oturum/queryClient'tadır,
// `authKeys`/`permitsKeys` ile AYNI varsayım).
//
// **S35 — opsiyonel `siteUuid` saha süzgeci (§35.2).** İki uç da artık opsiyonel `siteUuid` alır;
// firma içinde çözülür (yok/başka firma → `404 SITE_NOT_FOUND`), verilmezse §34 davranışı BİREBİR
// korunur. Taşeron FİRMA belgesi alanları (`documentsExpired`/`documentsExpiring`/
// `contractorsWith*`) **saha süzgecinden ETKİLENMEZ** (§35.2 normatif — bir sahaya değil firmaya
// aittir); istemci bunları saha seçiliyken "firma geneli" diye ETİKETLEMEK ZORUNDADIR
// (`DashboardPage.tsx`). Sorgu anahtarı `siteUuid`'i İÇERMELİDİR (`dashboardKeys.ts`) — aksi
// hâlde saha değişimi eski firma-geneli sayıları önbellekten sunardı.

import { api } from './client';
import type { PermitStatus } from './permits';

/**
 * İzin statüsü wire değerleri — `ExpertDashboardSummaryDto.permitsByStatus`'un YOĞUN haritasının
 * 12 anahtarı (§34.1). `PermitStatus` (permits.ts) ile AYNI birleşim; burada ayrıca dışa
 * aktarılır ki bu modül `permits.ts`'e (tip-only import ötesinde) bağımlı GÖRÜNMESİN.
 */
export type ExpertDashboardPermitStatus = PermitStatus;

/** (X) süresi yaklaşan izin kümesinin TAM sınırları (§34.0) — drill-through OLDUĞU GİBİ geçirir. */
export interface ExpertDashboardWindowDto {
  /** UTC ISO 8601 (§4). */
  from: string;
  /** UTC ISO 8601 (§4). */
  to: string;
}

/** `permitsCreatedPerDay` satırı — `date` takvim günü (`'YYYY-MM-DD'`, çağıranın zon'unda; §34.1). */
export interface ExpertDashboardDayCountDto {
  date: string;
  count: number;
}

/**
 * Kind başına §34.2 kalem sayısı (`actionItemCounts`, YENİ S34) — `kind` süzgecinden ETKİLENMEZ
 * (sekme rozetleri için). Kapı yokken değer `0`'dır, `null` DEĞİL (§34.1 normatif not).
 */
export interface ExpertActionItemCountsDto {
  entryBlocked: number;
  expiringPermit: number;
  pendingApproval: number;
}

/**
 * `GET /dashboard/summary` yanıtı — api-contract §34.1 (`ExpertDashboardSummaryDto`). Alan
 * kapıları normatiftir (dosya başlığı): `documentsExpired`/`documentsExpiring`/
 * `contractorsWithExpiredDocs`/`contractorsWithExpiringDocs` DÖRDÜ BİRLİKTE `contractors/list`
 * yoksa `null`; `enteredToday` `security/gate-result` yoksa `null`. Diğerleri (uç kapısı
 * `permits/list` zaten geçmiş olduğundan) HER ZAMAN sayıdır.
 *
 * ⛔ **Birim uyarısı (§34.1 normatif).** `documentsExpired`/`documentsExpiring` BELGE sayısıdır
 * (bir taşeronun birden çok dolmuş belgesi olabilir); `contractorsWithExpiredDocs`/
 * `contractorsWithExpiringDocs` TAŞERON sayısıdır ve `GET /contractors?status=Active&
 * compliance=expired|expiring`'in `filteredTotal`'ına EŞİTTİR. Bir KPI karosu bir listeye
 * bağlanıyorsa gösterdiği sayı **taşeron sayısı** olmak ZORUNDADIR; belge sayısı yalnız ikincil
 * bağlamsal metin olarak, listeyle eşitlik vaadi OLMADAN kullanılır.
 */
export interface ExpertDashboardSummaryDto {
  /** UTC ISO 8601 (§4) — sunucunun hesap anı. */
  asOf: string;
  /** IANA — günün kesildiği zon (principal `timezone` claim'i, yoksa `Europe/Istanbul`). */
  timezone: string;
  /** İstek `siteUuid`'inin yankısı (§35.2, YENİ) — firma genelinde `null`. */
  siteUuid: string | null;

  activePermits: number;
  awaitingApproval: number;
  expiringIn7Days: number;
  expiringWindow: ExpertDashboardWindowDto;

  personnelNotReady: number;
  personnelWithMissingDocs: number;

  documentsExpired: number | null;
  documentsExpiring: number | null;
  contractorsWithExpiredDocs: number | null;
  contractorsWithExpiringDocs: number | null;

  enteredToday: number | null;

  /** YOĞUN harita — 12 statünün HEPSİ her zaman anahtar olarak bulunur (sıfır dâhil). */
  permitsByStatus: Record<ExpertDashboardPermitStatus, number>;
  /** YOĞUN dizi — `length === 30` her zaman (bugün + önceki 29 gün, eskiden yeniye). */
  permitsCreatedPerDay: ExpertDashboardDayCountDto[];

  actionItemCounts: ExpertActionItemCountsDto;
}

/** `ExpertActionItemDto.kind` wire değerleri (§34.2) — üç tür ikili KESİŞİMSİZDİR. */
export type ExpertActionItemKind = 'entryBlocked' | 'expiringPermit' | 'pendingApproval';

/** `ExpertActionItemDto.reason` wire değerleri — yalnız `kind = 'entryBlocked'` iken dolu. */
export type ExpertActionItemReason = 'missingDocument' | 'missingTraining';

/**
 * `GET /dashboard/action-items` satırı — api-contract §34.2 (`ExpertActionItemDto`). Üç `kind`
 * ikili KESİŞİMSİZDİR; `supplierUserUuid`/`personnelName`/`reason`/`missingDocumentTypes`/
 * `missingTrainingTitles` YALNIZ `kind = 'entryBlocked'` iken dolu (diğer ikisinde `null`, boş
 * dizi DEĞİL).
 *
 * ⚠️ **`supplierUserUuid` = `SupplierUser.Uuid`** (§14 `WorkPermitPersonnelDto.uuid` ile AYNI
 * değer) — `WorkPermitPersonnel` bağ satırının kendi uuid'i DEĞİL (o iç kimliktir, asla dönmez;
 * bağlanırsa kapsanan kişide sessiz `404` üretir — bilinen tuzak).
 */
export interface ExpertActionItemDto {
  kind: ExpertActionItemKind;
  permitUuid: string;
  permitTitle: string;
  /** TÜRETİLMİŞ durum (§14; `ExpertDashboardPermitStatus` ile aynı wire). */
  permitStatus: ExpertDashboardPermitStatus;
  contractorUuid: string;
  contractorName: string;
  /** UTC ISO 8601 (§4) — HER kind'da doludur (geçmişte olabilir, özellikle `entryBlocked`). */
  dueAt: string;

  supplierUserUuid: string | null;
  personnelName: string | null;
  reason: ExpertActionItemReason | null;
  /** §27 `personnel` kayıt kodları — etiket §27 kaydından (`useDocumentTypes().label`). */
  missingDocumentTypes: string[] | null;
  /** Ada göre artan — `reason = 'missingTraining'` iken `[]` MEŞRUDUR (başlığı çözülemeyen eğitim). */
  missingTrainingTitles: string[] | null;
}

/**
 * `GET /dashboard/action-items` liste/filtre parametreleri — api-contract §34.2 + §3 paging.
 * `kind` tek değerli (camelCase); verilmezse/tanınmazsa üçü birden (`400` DÖNMEZ). `search`/
 * `sortBy`/`sortDir` YOKTUR (sıra sabit — §34.2 normatif sıra).
 */
export interface ExpertActionItemListParams {
  kind?: ExpertActionItemKind;
  /** Saha süzgeci (§35.2, YENİ) — verilmezse firma geneli. */
  siteUuid?: string;
  page?: number;
  pageSize?: number;
}

/** `GET /dashboard/summary` parametreleri (§34.1 + §35.2). */
export interface ExpertDashboardSummaryParams {
  /** Saha süzgeci (§35.2, YENİ) — firma içinde çözülür; yok/başka firma → `404 SITE_NOT_FOUND`. */
  siteUuid?: string;
}

/**
 * `GET /dashboard/summary` (`permits/list` uç kapısı) — KPI özeti (§34.1), opsiyonel `siteUuid`
 * saha süzgeciyle (§35.2). Grant yoksa `403 FORBIDDEN` (yeni kod yok). Alan kapıları asla `403`
 * üretmez — yalnız `null`.
 */
export function getDashboardSummary(params: ExpertDashboardSummaryParams = {}) {
  return api.get<ExpertDashboardSummaryDto>('dashboard/summary', {
    searchParams: { siteUuid: params.siteUuid },
  });
}

/**
 * `GET /dashboard/action-items` (`permits/list` uç kapısı) — sayfalı "aksiyon gerekli" listesi
 * (§34.2), opsiyonel `siteUuid` saha süzgeciyle (§35.2). Sıra sunucuda sabittir (dueAt artan,
 * sonra kind, sonra iç Id); istemci yeniden sıralamaz.
 */
export function listDashboardActionItems(params: ExpertActionItemListParams = {}) {
  return api.list<ExpertActionItemDto>('dashboard/action-items', {
    searchParams: {
      kind: params.kind,
      siteUuid: params.siteUuid,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}
